import { useEffect, useState, useCallback } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { integrationAPI, socialAPI } from '../lib/api';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Link2, CheckCircle2, XCircle, Settings, Unplug, Loader2, KeyRound, ExternalLink, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatDateLong } from '../lib/dateFormat';
import { PageSelectorDialog } from '../components/PageSelectorDialog';
import { ConnectedPagesPanel } from '../components/ConnectedPagesPanel';

const PLATFORM_INFO = {
  facebook: { name: 'Facebook', color: '#1877F2', icon: 'f', desc: 'Pages, Groups, Messenger', oauth: true },
  instagram: { name: 'Instagram', color: '#E1306C', icon: 'IG', desc: 'Feed, Stories, Reels (via Facebook)', oauth: true, linked: 'facebook' },
  linkedin: { name: 'LinkedIn', color: '#0A66C2', icon: 'in', desc: 'Company pages, Posts', oauth: false },
  twitter: { name: 'Twitter / X', color: '#000000', icon: 'X', desc: 'Tweets, Threads, Spaces', oauth: false },
  youtube: { name: 'YouTube', color: '#FF0000', icon: 'YT', desc: 'Videos, Shorts, Community', oauth: false },
  pinterest: { name: 'Pinterest', color: '#E60023', icon: 'P', desc: 'Pins, Boards', oauth: false },
  gmb: { name: 'Google My Business', color: '#1A73E8', icon: 'G', desc: 'Posts, Reviews, Updates', oauth: false },
  substack: { name: 'Substack', color: '#FF6719', icon: 'S', desc: 'Newsletters, Posts', oauth: false },
};

export default function IntegrationsPage() {
  const { currentWorkspace } = useWorkspace();
  const [integrations, setIntegrations] = useState([]);
  const [connectedIntegrations, setConnectedIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // D86 Iter B — page-selector dialog (opens after OAuth callback or when
  // the admin clicks "Manage Pages" on a connected FB card).
  const [pageSelectorOpen, setPageSelectorOpen] = useState(false);

  // Handle OAuth callback params
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const pending = searchParams.get('pending');
    const pages = searchParams.get('pages');
    const ig = searchParams.get('ig');
    if (success === 'facebook') {
      toast.success(`Facebook connected! ${pages || 0} page(s) and ${ig || 0} Instagram account(s) linked.`);
      window.history.replaceState({}, '', '/integrations');
    } else if (pending === 'facebook') {
      // D86 Iter B — open the page-selector immediately after OAuth.
      toast.success(`Authorized — pick which of the ${pages || 0} Page(s) to connect.`);
      setPageSelectorOpen(true);
      window.history.replaceState({}, '', '/integrations');
    } else if (error) {
      toast.error(`Connection failed: ${decodeURIComponent(error)}`);
      window.history.replaceState({}, '', '/integrations');
    }
  }, [searchParams]);

  const fetchIntegrations = useCallback(() => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    Promise.all([
      integrationAPI.list(currentWorkspace.id),
      api.get(`/integrations/${currentWorkspace.id}/connected`).catch(() => ({ data: [] }))
    ])
      .then(([credRes, connRes]) => {
        setIntegrations(credRes.data);
        setConnectedIntegrations(connRes.data);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [currentWorkspace?.id]);

  useEffect(() => { fetchIntegrations(); }, [fetchIntegrations]);

  const isOAuthConnected = (platform) => {
    return connectedIntegrations.some(c => c.platform === platform && c.status === 'connected');
  };

  const getOAuthData = (platform) => {
    return connectedIntegrations.find(c => c.platform === platform && c.status === 'connected');
  };

  const handleConnectFacebook = async () => {
    if (!currentWorkspace?.id) return;
    setConnecting('facebook');
    try {
      const res = await api.get(`/auth/facebook/init?workspace_id=${currentWorkspace.id}`);
      // Redirect to Facebook OAuth
      window.location.href = res.data.auth_url;
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to start Facebook connection';
      toast.error(detail);
      setConnecting(null);
    }
  };

  const handleConnect = async (platform) => {
    if (platform === 'facebook' || platform === 'instagram') {
      handleConnectFacebook();
      return;
    }
    const info = PLATFORM_INFO[platform];
    const integration = integrations.find(i => i.platform === platform);
    if (!integration?.has_credentials) {
      toast.error(`No API credentials found for ${info?.name}. Please add them in Settings first.`, {
        action: { label: 'Go to Settings', onClick: () => navigate('/settings?tab=credentials') },
      });
      return;
    }
    setConnecting(platform);
    try {
      await integrationAPI.connect(currentWorkspace.id, platform);
      toast.success(`${info?.name} connected successfully!`);
      fetchIntegrations();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to connect ${info?.name}`);
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (platform) => {
    try {
      await api.post(`/integrations/${currentWorkspace.id}/disconnect/${platform}`);
      toast.success(`Disconnected ${PLATFORM_INFO[platform]?.name}`);
      fetchIntegrations();
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const fbConnected = isOAuthConnected('facebook');
  const igConnected = isOAuthConnected('instagram');
  const credConnectedCount = integrations.filter(i => i.connected).length;
  const oauthConnectedCount = (fbConnected ? 1 : 0) + (igConnected ? 1 : 0);
  const totalConnected = credConnectedCount + oauthConnectedCount;

  const renderPlatformCard = (platformKey) => {
    const info = PLATFORM_INFO[platformKey];
    const credIntegration = integrations.find(i => i.platform === platformKey);
    const oauthConnected = isOAuthConnected(platformKey);
    const oauthData = getOAuthData(platformKey);
    const isConnecting = connecting === platformKey;
    const isLinkedToFacebook = platformKey === 'instagram';
    const connected = oauthConnected || credIntegration?.connected;

    return (
      <Card
        key={platformKey}
        className="bg-card/60 backdrop-blur border-l-4 transition-shadow hover:shadow-md"
        style={{ borderLeftColor: info.color }}
        data-testid={`integration-card-${platformKey}`}
      >
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div
                className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                style={{ backgroundColor: info.color }}
              >
                {info.icon}
              </div>
              <div>
                <p className="font-medium text-sm">{info.name}</p>
                <p className="text-xs text-muted-foreground">{info.desc}</p>
              </div>
            </div>
            {connected ? (
              <Badge className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 text-xs" data-testid={`badge-connected-${platformKey}`}>
                <CheckCircle2 className="h-3 w-3 mr-1" /> Connected
              </Badge>
            ) : credIntegration?.has_credentials ? (
              <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/20 text-xs" data-testid={`badge-ready-${platformKey}`}>
                <KeyRound className="h-3 w-3 mr-1" /> Ready
              </Badge>
            ) : info.oauth ? (
              <Badge className="bg-blue-500/15 text-blue-400 border border-blue-500/20 text-xs">
                <Globe className="h-3 w-3 mr-1" /> OAuth
              </Badge>
            ) : (
              <Badge className="bg-secondary text-muted-foreground text-xs">
                <XCircle className="h-3 w-3 mr-1" /> No Keys
              </Badge>
            )}
          </div>

          {/* Show connected account details */}
          {oauthConnected && oauthData && (
            <div className="mb-3 p-2.5 rounded-lg bg-emerald-500/5 border border-emerald-500/10 text-xs space-y-1">
              {platformKey === 'facebook' && oauthData.pages?.length > 0 && (
                <>
                  <p className="font-medium text-emerald-400" data-testid="fb-pages-summary">
                    {oauthData.pages.length} Page{oauthData.pages.length === 1 ? '' : 's'} connected
                    {oauthData.pages.filter(p => p?.ig_username).length > 0 && (
                      <span className="text-pink-400/90 ml-1">
                        · {oauthData.pages.filter(p => p?.ig_username).length} with Instagram
                      </span>
                    )}
                  </p>
                  <p className="text-muted-foreground">Manage below ↓</p>
                </>
              )}
              {platformKey === 'instagram' && oauthData.accounts?.length > 0 && (
                <>
                  <p className="font-medium text-emerald-400">{oauthData.accounts.length} Account(s) Connected</p>
                  {oauthData.accounts.map(a => (
                    <p key={a.id} className="text-muted-foreground pl-2">• @{a.username || a.name}</p>
                  ))}
                </>
              )}
              {oauthData.connected_at && (
                <p className="text-muted-foreground">Connected {formatDateLong(oauthData.connected_at)}</p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            {connected ? (
              <>
                {platformKey === 'facebook' && (
                  <Button variant="outline" size="sm" className="flex-1"
                    onClick={() => setPageSelectorOpen(true)}
                    data-testid="manage-fb-pages">
                    <Settings className="h-4 w-4 mr-2" /> Manage Pages
                  </Button>
                )}
                <Button variant="outline" size="sm" className={platformKey === 'facebook' ? 'flex-1 text-destructive' : 'w-full text-destructive'}
                  onClick={() => handleDisconnect(platformKey)}
                  data-testid={`disconnect-${platformKey}`}>
                  <Unplug className="h-4 w-4 mr-2" /> Disconnect
                </Button>
              </>
            ) : info.oauth ? (
              <Button size="sm" className="w-full" disabled={isConnecting}
                onClick={() => handleConnect(platformKey)}
                data-testid={`connect-${platformKey}`}>
                {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                {isConnecting ? 'Connecting...' : isLinkedToFacebook ? 'Connect via Facebook' : 'Connect with Facebook'}
              </Button>
            ) : credIntegration?.has_credentials ? (
              <Button size="sm" className="w-full" disabled={isConnecting}
                onClick={() => handleConnect(platformKey)}
                data-testid={`connect-${platformKey}`}>
                {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
                {isConnecting ? 'Connecting...' : 'Connect'}
              </Button>
            ) : (
              <Button variant="outline" size="sm" className="w-full"
                onClick={() => navigate('/settings?tab=credentials')}
                data-testid={`setup-${platformKey}`}>
                <Settings className="h-4 w-4 mr-2" /> Add API Keys
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-6 space-y-6 animate-fade-in-up" data-testid="integrations-page">
      <div>
        <h1 className="text-2xl font-display font-semibold">Integrations</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {totalConnected}/{Object.keys(PLATFORM_INFO).length} platforms connected
          {totalConnected === 0 && '. Connect your social accounts to start publishing.'}
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(8)].map((_, i) => <div key={i} className="h-44 bg-secondary rounded-xl animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.keys(PLATFORM_INFO).map(key => renderPlatformCard(key))}
        </div>
      )}

      {/* D90 — Connected Pages bulk-management panel (filter + select-all
          + bulk-assign to a Client Group with inline create). Shown
          when a Facebook integration exists with at least one Page. */}
      {connectedIntegrations.some((c) => c.platform === 'facebook' && c.status === 'connected') && (
        <ConnectedPagesPanel
          workspaceId={currentWorkspace?.id}
          onChanged={fetchIntegrations}
        />
      )}

      <Card className="bg-card/60 backdrop-blur">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center shrink-0">
              <KeyRound className="h-4 w-4 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium text-sm">How Integrations Work</p>
              <div className="text-xs text-muted-foreground mt-1 space-y-1">
                <p><strong className="text-gold">Facebook & Instagram:</strong> Click "Connect with Facebook" to authorize directly via OAuth. This connects your pages and Instagram business accounts.</p>
                <p><strong className="text-foreground">Other platforms:</strong> Add API credentials in{' '}
                  <button className="text-gold underline underline-offset-2" onClick={() => navigate('/settings?tab=credentials')}>Settings → API Credentials</button>
                  , then click Connect.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* D86 Iter B — Facebook page selector + client group assignment */}
      <PageSelectorDialog
        workspaceId={currentWorkspace?.id}
        open={pageSelectorOpen}
        onClose={() => setPageSelectorOpen(false)}
        onAttached={() => fetchIntegrations()}
      />
    </div>
  );
}
